<template>
  <div>
    <div class="page-business-intelligence-graph">
      <BaseLoadingBar v-if="chartsPending" />
      <template v-else>
        <div class="page-business-intelligence-graph__charts-container" v-if="hasChartsResult">
          <div class="page-business-intelligence-graph__charts-container--left">
            <div class="page-business-intelligence-graph__column-title">
              {{ $t('page.business-intelligence.performances') }}
            </div>
            <apexchart
              v-for="(chart, key) in leftCharts"
              :key="key"
              class="page-business-intelligence-graph__charts-container--chart"
              :type="chart.options.chart.type"
              :options="chart.options"
              :series="chart.series"
            />
          </div>
          <div class="page-business-intelligence-graph__charts-container--right">
            <div class="page-business-intelligence-graph__column-title">
              {{ $t('page.business-intelligence.performances-compared') }}
            </div>
            <apexchart
              v-for="(chart, key) in rightCharts"
              :key="key"
              class="page-business-intelligence-graph__charts-container--chart"
              :type="chart.options.chart.type"
              :options="chart.options"
              :series="chart.series"
              @dataPointMouseEnter="
                (event, chartContext, args) => handleDataPointHover(chart.id, event, chartContext, args)
              "
            />
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';
import BaseLoadingBar from '@/components/base/BaseLoadingBar.vue';
import { formatStandardDate } from '@/util/date-util';
import moment from 'moment';

export default {
  name: 'PageBIGraph',
  components: {
    BaseLoadingBar,
  },
  computed: {
    ...mapGetters('biDashboard', ['charts', 'dataPointValues']),
    ...mapGetters('biDashboard', { chartsPending: 'pending' }),
    ...mapGetters('tenant', ['tenant']),
    ...mapGetters('auth', ['currentUser']),
    hasChartsResult() {
      return this.charts.length > 0;
    },
    tooltipContent() {
      if (this.dataPointValues.length > 0) {
        const rows = this.dataPointValues
          .map(
            (value) => `<tr>
                                      <td>${value.projectName}</td>
                                      <td>${value.clientName}</td>
                                      <td>${formatStandardDate(value.tenderDate)}</td>
                                      <td class="number-monospace">${this.formatCurrency(value.awardAmount)}</td>
                                    </tr>`,
          )
          .join('');
        return `<table class="chart-tooltip__container">
                  <tr>
                    <th>${this.$t('page.business-intelligence.awards.project')}</th>
                    <th>${this.$t('page.business-intelligence.awards.client')}</th>
                    <th>${this.$t('page.business-intelligence.awards.tender-date')}</th>
                    <th class="number-monospace">${this.$t('page.business-intelligence.awards.amount')}</th>
                  </tr>
                  ${rows}
                </table>`;
      }

      return `<div class="chart-tooltip__container">
                                                <div class="apexcharts-tooltip-text-y-value">${this.$t(
                                                  'page.business-intelligence.awards.none',
                                                )}</div>
                                               </div>`;
    },
  },
  data() {
    return {
      leftCharts: [],
      rightCharts: [],
      lastHoveredPoint: null,
      hoverTimeout: null,
    };
  },
  methods: {
    ...mapActions('biDashboard', ['getAllCharts', 'getSelectedCharts', 'getDataFromDataPoint']),
    separateChartsByCategory() {
      this.charts.forEach((chart) => {
        if (chart.id === 'weighted-diff-per-month' || chart.id === 'cumul-amount-provider') {
          this.leftCharts.push(chart);
        } else {
          this.rightCharts.push(chart);
        }
      });
    },
    formatCurrency(number) {
      const formattedNumber = new Intl.NumberFormat('fr-FR', {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(number);
      return `${formattedNumber}$`;
    },
    updateTooltip(
      chartId,
      content,
      title = this.$t('page.business-intelligence.awards.additional-info'),
      cumulativeAmount,
    ) {
      const tooltipEl = document.getElementById(`tooltip-content-custom-${chartId}`);
      if (!tooltipEl) {
        return;
      }

      tooltipEl.innerHTML = `<div class="apexcharts-tooltip-title">
                                <div>${title}</div>
                                <div class="chart-tooltip__cumulative-amount">
                                    <span>${this.formatCurrency(cumulativeAmount)}</span>
                                    <span class="chart-tooltip__cumulative-amount-label">${this.$t(
                                      'page.business-intelligence.awards.cumulative',
                                    )}</span>
                                </div>
                             </div>
                          <div class="apexcharts-tooltip-text-y-value">${content}</div>`;
    },
    async handleDataPointHover(chartId, event, chartContext, { seriesIndex, dataPointIndex, w }) {
      const supplier = w.config.series[seriesIndex].name;
      const [year, month] = w.config.xaxis.categories[dataPointIndex].split('-');
      const cumulative = w.config.series[seriesIndex].data[dataPointIndex];
      const formattedDate = moment(w.config.xaxis.categories[dataPointIndex], 'YYYY-MM').format('MMMM YYYY');

      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout);
      }

      if (this.lastHoveredPoint && this.lastHoveredPoint.id === `${year}-${month}-${seriesIndex}-${dataPointIndex}`) {
        this.hoverTimeout = setTimeout(async () => {
          if (!this.lastHoveredPoint.data) {
            await this.getDataFromDataPoint({ supplier, month, year });
          }
          this.updateTooltip(chartId, this.tooltipContent, formattedDate, cumulative);
        }, 500);
        return;
      }

      this.lastHoveredPoint = {
        id: `${year}-${month}-${seriesIndex}-${dataPointIndex}`,
        data: null,
      };

      this.hoverTimeout = setTimeout(async () => {
        await this.getDataFromDataPoint({ supplier, month, year });
        this.lastHoveredPoint.data = this.dataPointValues;
        this.updateTooltip(chartId, this.tooltipContent, formattedDate, cumulative);
      }, 500);
    },
  },
  async created() {
    await this.getAllCharts();
  },
  watch: {
    charts() {
      if (this.charts.length === 0) {
        this.leftCharts = [];
        this.rightCharts = [];
      } else {
        this.separateChartsByCategory();
      }
    },
  },
};
</script>

<style lang="scss">
.page-business-intelligence-graph {
  padding: 0 32px 32px 32px;

  // Phones: the screen's gutter, one column of charts, and tooltips no wider than the screen.
  @include screen-below('tablet') {
    padding: 0 $phone-page-gutter $phone-page-gutter;

    .page-business-intelligence-graph__charts-container--left,
    .page-business-intelligence-graph__charts-container--right {
      width: 100%;
    }

    .page-business-intelligence-graph__charts-container--chart {
      padding: $phone-page-gutter;
    }
  }

  &__column-title {
    font-weight: bold;
    font-size: 18px;
    margin-bottom: 16px;
  }
  &__charts-container {
    position: relative;
    justify-content: space-between;
    z-index: 1;
    display: flex;
    padding-top: 32px;
    flex-wrap: wrap;
    &--left,
    &--right {
      width: calc(50% - 8px);
    }
    &--chart {
      background-color: $color-white;
      border-radius: $border-radius-root;
      padding: 32px;
      margin-bottom: 16px;
      width: 100%;
    }
  }
}
.chart-tooltip {
  &__container {
    min-width: 25rem;

    @include screen-below('tablet') {
      min-width: 0;
      max-width: calc(100vw - #{2 * $phone-page-gutter});
    }
    padding: 0.5rem;
    border-spacing: 1rem;
    text-align: left;

    th {
      color: $color-neutral;
      font-size: $font-size-root;
      font-weight: normal;
    }
    tr {
      color: $color-normal;
      font-size: $font-size-xsmall;
      font-weight: 600;
    }
  }
  &__cumulative-amount {
    display: flex;
    flex-direction: column;
    align-items: flex-end;

    &-label {
      color: $color-dark-blue;
      font-size: 0.75rem;
      font-weight: 400;
    }
  }
}
.apexcharts-tooltip-title {
  color: $color-normal;
  font-size: $font-size-xbigger;
  font-weight: 800;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}
.apexcharts-tooltip-text-y-value {
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  span {
    font-weight: bold;
  }
}
@media screen and (max-width: 1080px) {
  .page-business-intelligence-graph {
    &__charts-container {
      flex-wrap: wrap;
      &--left {
        width: 100%;
      }
      &--right {
        width: 100%;
      }
    }
  }
}
</style>
